import { spacing, typography, borderRadius } from "../app/design-system";
import type { CSSProperties } from "react";
import { useEffect,useMemo,useState } from "react";
import type { RgbColor } from "react-colorful";
import { HexColorPicker as ReactColorfulHexColorPicker } from "react-colorful";
import { BgColorsOutlined } from "@ant-design/icons";
import { Button,Input,InputNumber,Popover,Typography,theme } from "../../../../../shared/frontend/ui";
import { useI18n } from "../app/i18n/useI18n";

const SHORT_HEX_LENGTH = 3;
const HEX_RADIX = 16;
const HEX_CHANNEL_WIDTH = 2;
// Zeichenpositionen der Kanäle in "#rrggbb".
const HEX_RED_START = 1;
const HEX_GREEN_START = 3;
const HEX_BLUE_START = 5;
const HEX_BLUE_END = 7;
const MAX_CHANNEL_VALUE = 255;
const TRANSPARENT_COLOR = "transparent";
/** Mischanteile für die helleren (Weiß) und dunkleren (Schwarz) Abstufungen. */
const TINT_STEPS = [0.82, 0.58, 0.32] as const;
const SHADE_STEPS = [0.25, 0.48, 0.68] as const;

const DEFAULT_HEX = "#1677ff";

export const HEX_COLOR_PRESETS = [
  "#1677ff", "#52c41a", "#ff4d4f", "#fa8c16", "#13c2c2",
  "#722ed1", "#eb2f96", "#a0d911", "#faad14", "#fa541c",
  "#2f54eb", "#1890ff", "#73d13d", "#ff7a45", "#36cfc9",
  "#597ef7", "#9254de", "#f759ab", "#bae637", "#ffc53d",
];

const NAME_TO_HEX: Record<string, string> = {
  blue: "#1677ff",
  green: "#52c41a",
  red: "#ff4d4f",
  orange: "#fa8c16",
  cyan: "#13c2c2",
  purple: "#722ed1",
  magenta: "#eb2f96",
  lime: "#a0d911",
  gold: "#faad14",
  volcano: "#fa541c",
  geekblue: "#2f54eb",
  success: "#52c41a",
  warning: "#faad14",
  error: "#ff4d4f",
  processing: "#1677ff",
};

const RGB_CHANNELS: Array<{ key: keyof RgbColor; label: string; textKey: string }> = [
  { key: "r", label: "R", textKey: "Color picker red channel" },
  { key: "g", label: "G", textKey: "Color picker green channel" },
  { key: "b", label: "B", textKey: "Color picker blue channel" },
];

export function isHexColor(value: string | null | undefined): boolean {
  return !!normalizeHexColor(value);
}

export function toDisplayHexColor(value: string | null | undefined): string | null {
  const normalized = normalizeHexColor(value);
  if (normalized) return normalized;
  const lower = value?.toLowerCase().trim();
  return lower ? NAME_TO_HEX[lower] ?? null : null;
}

function normalizeHexColor(value: string | null | undefined): string | null {
  const raw = (value ?? "").trim();
  const match = raw.match(/^#?([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/);
  if (!match) return null;
  const hex = match[1];
  const full = hex.length === SHORT_HEX_LENGTH
    ? `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`
    : hex;
  return `#${full.toLowerCase()}`;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function hexToRgb(hex: string): RgbColor {
  const normalized = normalizeHexColor(hex) ?? DEFAULT_HEX;
  return {
    r: parseInt(normalized.slice(HEX_RED_START, HEX_GREEN_START), HEX_RADIX),
    g: parseInt(normalized.slice(HEX_GREEN_START, HEX_BLUE_START), HEX_RADIX),
    b: parseInt(normalized.slice(HEX_BLUE_START, HEX_BLUE_END), HEX_RADIX),
  };
}

function channelToHex(channel: number): string {
  return clamp(Math.round(channel), 0, MAX_CHANNEL_VALUE).toString(HEX_RADIX).padStart(HEX_CHANNEL_WIDTH, "0");
}

function rgbToHex({ r, g, b }: RgbColor): string {
  return `#${channelToHex(r)}${channelToHex(g)}${channelToHex(b)}`;
}

function mixRgb(from: RgbColor, to: RgbColor, amount: number): RgbColor {
  return {
    r: from.r + (to.r - from.r) * amount,
    g: from.g + (to.g - from.g) * amount,
    b: from.b + (to.b - from.b) * amount,
  };
}

/**
 * Zahleneingabe für einen RGB-Kanal, die erst beim Verlassen oder mit Enter übernimmt.
 *
 * Direkt durchgereichte Eingaben haben bei jedem Tastendruck eine neue Farbe gesetzt und das Feld
 * dabei aus der laufenden Eingabe heraus überschrieben — „25“ wurde als 2 übernommen und korrigiert.
 */
function RgbChannelInput({
  label,
  ariaLabel,
  value,
  onCommit,
}: {
  label: string;
  ariaLabel: string;
  value: number;
  onCommit: (next: number) => void;
}) {
  const [draft, setDraft] = useState<number | null>(value);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Abgleich mit dem Wert von außen
    setDraft(value);
  }, [value]);

  const commit = () => {
    if (typeof draft === "number" && !Number.isNaN(draft)) onCommit(clamp(Math.round(draft), 0, MAX_CHANNEL_VALUE));
    else setDraft(value);
  };

  return (
    <label>
      <Typography.Text type="secondary" style={{ display: "block", fontSize: typography.fontSize.sm, marginBottom: spacing.xs }}>
        {label}
      </Typography.Text>
      <InputNumber
        aria-label={ariaLabel}
        controls={false}
        min={0}
        max={255}
        value={draft}
        onChange={(next) => setDraft(typeof next === "number" ? next : null)}
        onBlur={commit}
        onPressEnter={commit}
        size="small"
        style={{ width: "100%" }}
      />
    </label>
  );
}

export type HexColorPickerProps = {
  /**
   * Kontrollierter Farbwert. Ohne `value` hält die Standardkomponente die zuletzt gewählte Farbe
   * selbst; das ist für unmittelbare Werkzeugleistenaktionen ohne eigenes Formularfeld gedacht.
   */
  value?: string | null;
  onChange?: (value: string | null) => void;
  emptyValue?: string | null;
  allowClear?: boolean;
  /** Benannte Zustände neben einer konkreten Farbe, etwa „Standard" und „keine Farbe". */
  nonColorOptions?: Array<{ value: string | null; label: string }>;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  size?: "small" | "middle" | "large";
  showValueText?: boolean;
  triggerMode?: "field" | "swatch";
  placeholder?: string;
  /** Optionales Symbol für den Swatch-Trigger (z. B. Buchstabe A oder Farbeimer mit Farbbalken darunter). */
  icon?: React.ReactNode;
  /**
   * Wann die Wahl gemeldet wird. `live` (Vorgabe) meldet jede Bewegung im Farbfeld — richtig für
   * ein Formularfeld, dessen Wert erst beim Speichern wirkt.
   * <p>
   * `explicit` meldet erst beim Übernehmen. Nötig, wo die Farbe sofort auf eine Textmarkierung
   * wirkt: Dort setzte jede Zwischenfarbe ein eigenes Kommando ab, und nach dem ersten war die
   * Markierung weg — die eigentlich gewählte Farbe landete dann nirgends.
   * </p>
   */
  commitMode?: "live" | "explicit";
};

export function HexColorPicker({
  value,
  onChange,
  emptyValue = null,
  allowClear = true,
  nonColorOptions,
  disabled = false,
  className,
  style,
  size = "middle",
  showValueText = true,
  triggerMode = "field",
  placeholder,
  icon,
  commitMode = "live",
}: HexColorPickerProps) {
  const { text } = useI18n();
  const { token } = theme.useToken();
  const [open, setOpen] = useState(false);
  const [uncontrolledValue, setUncontrolledValue] = useState<string | null>(value ?? null);
  const [pendingValue, setPendingValue] = useState<string | null>(value ?? null);
  const explicitCommit = commitMode === "explicit";
  const storedValue = value === undefined ? uncontrolledValue : value;
  // Solange die Wahl offen ist und erst übernommen werden muss, zeigt das Feld den Entwurf.
  const effectiveValue = explicitCommit && open ? pendingValue : storedValue;
  const displayHex = toDisplayHexColor(effectiveValue);
  const activeNonColorOption = nonColorOptions?.find((option) => option.value === effectiveValue);
  const transparent = effectiveValue?.trim().toLowerCase() === TRANSPARENT_COLOR;
  const activeHex = displayHex ?? DEFAULT_HEX;
  const activeRgb = useMemo(() => hexToRgb(activeHex), [activeHex]);

  const commitHex = (nextHex: string | null) => {
    const committed = nextHex ? nextHex.toLowerCase() : emptyValue;
    if (explicitCommit && open) {
      setPendingValue(committed);
      return;
    }

    if (value === undefined) setUncontrolledValue(committed);
    onChange?.(committed);
  };

  const applyPending = () => {
    if (value === undefined) setUncontrolledValue(pendingValue);
    onChange?.(pendingValue);
    setOpen(false);
  };

  const selectNonColorValue = (nextValue: string | null) => {
    // Ein benannter Zustand ist eine fertige Entscheidung und kein Farbentwurf. Er wird deshalb
    // auch im expliziten Modus sofort gemeldet; andernfalls zeigte der Dialog bis „Übernehmen"
    // seine blaue interne Startfarbe, obwohl bereits zurückgesetzt wurde.
    if (value === undefined) setUncontrolledValue(nextValue);
    setPendingValue(nextValue);
    onChange?.(nextValue);
    setOpen(false);
  };

  const commitRgb = (nextRgb: Partial<RgbColor>) => {
    commitHex(rgbToHex({ ...activeRgb, ...nextRgb }));
  };

  /**
   * Eigener Entwurfszustand für das Hex-Feld: Die Eingabe wird erst beim Verlassen oder mit Enter
   * übernommen. Vorher wurde bei jedem Zeichen geprüft und übernommen — auf halbem Weg traf „#1a2“
   * als Kurzschreibweise zu und sprang auf #11AA22, danach schrieb der Abgleich von außen das Feld
   * wieder um. Eine Farbe war so kaum eintippbar.
   */
  const [hexDraft, setHexDraft] = useState(displayHex?.toUpperCase() ?? "");

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Abgleich mit dem Wert von außen
    setHexDraft(displayHex?.toUpperCase() ?? "");
  }, [displayHex]);

  const commitHexDraft = () => {
    const normalized = normalizeHexColor(hexDraft);
    if (normalized) commitHex(normalized);
    // Unvollständige oder ungültige Eingabe verwerfen statt raten.
    else setHexDraft(displayHex?.toUpperCase() ?? "");
  };

  const tonalVariants = useMemo(() => {
    const white = { r: MAX_CHANNEL_VALUE, g: MAX_CHANNEL_VALUE, b: MAX_CHANNEL_VALUE };
    const black = { r: 0, g: 0, b: 0 };
    return [
      rgbToHex(mixRgb(activeRgb, white, TINT_STEPS[0])),
      rgbToHex(mixRgb(activeRgb, white, TINT_STEPS[1])),
      rgbToHex(mixRgb(activeRgb, white, TINT_STEPS[2])),
      activeHex,
      rgbToHex(mixRgb(activeRgb, black, SHADE_STEPS[0])),
      rgbToHex(mixRgb(activeRgb, black, SHADE_STEPS[1])),
      rgbToHex(mixRgb(activeRgb, black, SHADE_STEPS[2])),
    ];
  }, [activeHex, activeRgb]);

  const nonColorBackground = transparent
    ? `linear-gradient(135deg, transparent 45%, ${token.colorBorder} 46%, ${token.colorBorder} 54%, transparent 55%)`
    : token.colorFillQuaternary;
  const nonColorLabel = activeNonColorOption?.label ?? text("No custom color");

  const swatch = icon && triggerMode === "swatch" ? (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
      <span style={{ fontSize: typography.fontSize.sm, lineHeight: typography.lineHeight.icon, color: token.colorText, marginBottom: spacing.micro }}>
        {icon}
      </span>
      <span
        aria-hidden
        style={{
          width: 18,
          height: 3,
          borderRadius: borderRadius.xs,
          background: displayHex ?? nonColorBackground,
          border: `1px solid ${displayHex ? token.colorBorderSecondary : token.colorBorder}`,
          boxSizing: "border-box",
        }}
      />
    </div>
  ) : (
    <span
      aria-hidden
      style={{
        width: triggerMode === "swatch" ? "100%" : 18,
        height: triggerMode === "swatch" ? "100%" : 18,
        borderRadius: triggerMode === "swatch" ? 5 : 4,
        background: displayHex ?? nonColorBackground,
        border: `1px solid ${token.colorBorderSecondary}`,
        boxSizing: "border-box",
        display: "inline-block",
        flex: "0 0 auto",
      }}
    />
  );

  const trigger = triggerMode === "swatch" ? (
    <button
      type="button"
      className={className}
      disabled={disabled}
      onClick={() => setOpen(true)}
      style={{
        width: 32,
        height: 32,
        padding: spacing.compact,
        borderRadius: borderRadius.md,
        border: `1px solid ${token.colorBorder}`,
        background: token.colorBgContainer,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        ...style,
      }}
      title={displayHex || activeNonColorOption?.label || effectiveValue || placeholder}
    >
      {swatch}
    </button>
  ) : (
    <Button
      className={className}
      disabled={disabled}
      icon={swatch}
      onClick={() => setOpen(true)}
      size={size}
      style={{ justifyContent: "flex-start", textAlign: "left", ...style }}
      title={displayHex || activeNonColorOption?.label || effectiveValue || placeholder}
    >
      {showValueText ? (displayHex?.toUpperCase() ?? placeholder ?? text("None")) : <BgColorsOutlined />}
    </Button>
  );

  const panel = (
    <div style={{ width: 304 }}>
      <div style={{ display: "flex", gap: spacing.md, alignItems: "center", marginBottom: spacing.md }}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: borderRadius.lg,
            background: displayHex ?? nonColorBackground,
            border: `1px solid ${token.colorBorderSecondary}`,
            boxShadow: token.boxShadowTertiary,
          }}
          title={displayHex?.toUpperCase() ?? nonColorLabel}
        />
        {/* Kein Hex-Wert hier — der steht direkt darunter im Eingabefeld und wäre doppelt. */}
        <div style={{ minWidth: 0 }}>
          <Typography.Text type="secondary" style={{ fontSize: typography.fontSize.sm }}>
            {displayHex ? text("Selected color") : nonColorLabel}
          </Typography.Text>
        </div>
        <div style={{ display: "flex", gap: spacing.xs, marginInlineStart: "auto" }}>
          {nonColorOptions?.map((option) => (
            <Button
              key={option.value ?? "__null"}
              size="small"
              type={option === activeNonColorOption ? "primary" : "text"}
              onClick={() => selectNonColorValue(option.value)}
            >
              {option.label}
            </Button>
          ))}
          {allowClear && !nonColorOptions ? (
            <Button size="small" type="text" onClick={() => selectNonColorValue(emptyValue)}>
              {text("Remove color")}
            </Button>
          ) : null}
        </div>
      </div>

      <ReactColorfulHexColorPicker
        color={activeHex}
        onChange={(nextHex) => commitHex(normalizeHexColor(nextHex) ?? activeHex)}
        style={{ width: "100%" }}
      />

      <div style={{ display: "grid", gridTemplateColumns: displayHex ? "1fr 58px 58px 58px" : "1fr", gap: spacing.sm, marginTop: spacing.md }}>
        <label>
          <Typography.Text type="secondary" style={{ display: "block", fontSize: typography.fontSize.sm, marginBottom: spacing.xs }}>
            {text("Color picker hex value")}
          </Typography.Text>
          <Input
            value={hexDraft}
            onChange={(e) => setHexDraft(e.target.value)}
            onBlur={commitHexDraft}
            onPressEnter={commitHexDraft}
            size="small"
            spellCheck={false}
            style={{ width: "100%", fontSize: typography.fontSize.sm }}
          />
        </label>
        {displayHex ? RGB_CHANNELS.map(({ key, label, textKey }) => (
          <RgbChannelInput
            key={key}
            label={label}
            ariaLabel={text(textKey)}
            value={activeRgb[key]}
            onCommit={(next) => commitRgb({ [key]: next } as Partial<RgbColor>)}
          />
        )) : null}
      </div>

      <div style={{ marginTop: spacing.md }}>
        <Typography.Text type="secondary" style={{ display: "block", fontSize: typography.fontSize.sm, marginBottom: spacing.controlCompact }}>
          {text("Color picker tonal variants")}
        </Typography.Text>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: spacing.controlCompact }}>
          {tonalVariants.map((hex) => (
            <button
              key={hex}
              type="button"
              onClick={() => commitHex(hex)}
              style={{
                height: 24,
                borderRadius: borderRadius.compact,
                border: displayHex && hex === activeHex ? `2px solid ${token.colorPrimary}` : `1px solid ${token.colorBorderSecondary}`,
                background: hex,
                cursor: "pointer",
              }}
              title={hex.toUpperCase()}
            />
          ))}
        </div>
      </div>

      <div style={{ marginTop: spacing.md }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(10, 1fr)", gap: spacing.minor }}>
          {HEX_COLOR_PRESETS.map((hex) => (
            <button
              key={hex}
              type="button"
              onClick={() => commitHex(hex)}
              style={{
                width: 24,
                height: 24,
                borderRadius: borderRadius.compact,
                border: displayHex && hex === activeHex ? `2px solid ${token.colorPrimary}` : `1px solid ${token.colorBorderSecondary}`,
                background: hex,
                cursor: "pointer",
              }}
              title={hex.toUpperCase()}
            />
          ))}
        </div>
      </div>

      {explicitCommit ? (
        <div style={{ display: "flex", justifyContent: "flex-end", gap: spacing.sm, marginTop: spacing.md }}>
          <Button size="small" onClick={() => setOpen(false)}>{text("Cancel")}</Button>
          <Button size="small" type="primary" onClick={applyPending}>{text("Apply")}</Button>
        </div>
      ) : null}
    </div>
  );

  return (
    <Popover
      content={panel}
      trigger="click"
      open={open}
      onOpenChange={(nextOpen) => {
        if (disabled) return;
        if (nextOpen && explicitCommit) setPendingValue(storedValue);
        setOpen(nextOpen);
      }}
      placement="bottomLeft"
    >
      {trigger}
    </Popover>
  );
}
